import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "messages", "recordButton", "stopButton", "timer", "timerDisplay", "sendButton", "voiceSelector", "vibeSelector"]
  static classes = ["loading"]

  connect() {
    console.log("Bartender chat controller connected!")
    this.scrollToBottom()

    // Initialize recording state
    this.recordingState = "idle" // idle | recording | processing
    this.mediaRecorder = null
    this.audioChunks = []
    this.recordingStartTime = null
    this.timerInterval = null
    this.maxRecordingSeconds = 60

    // Loading indicator state
    this.loadingStartTime = null
    this.loadingInterval = null
    this.loadingElement = null

    // Initialize voice/vibe preferences (default values)
    this.selectedVoice = "bourbon_baritone"
    this.selectedVibe = "classic_host"

    // Setup voice/vibe selectors if they exist
    if (this.hasVoiceSelectorTarget) {
      this.voiceSelectorTarget.addEventListener("change", (e) => this.handleVoiceChange(e))
    }
    if (this.hasVibeSelectorTarget) {
      this.vibeSelectorTarget.addEventListener("change", (e) => this.handleVibeChange(e))
    }

    // Initialize audio streaming state (progressive playback)
    this.currentResponseId = null
    this.currentTtsFormat = "pcm"

    // Streaming queue state
    this.audioQueue = []              // Pending PCM buffers waiting to be scheduled
    this.isPlaybackActive = false     // Whether any audio is currently playing
    this.playbackStarted = false      // Whether first chunk has started playing
    this.queuedChunkCount = 0         // Total chunks queued this session
    this.playedChunkCount = 0         // Total chunks played this session
    this.streamComplete = false       // Whether server sent stream_complete
    this.initialBufferSize = 2        // Wait for N chunks before starting playback
    this.silencePaddingMs = 50        // Milliseconds of silence to prepend to first chunk

    // Chunk buffering for out-of-order delivery
    this.chunkBuffer = new Map()      // Map<chunkNumber, chunkBytes> for out-of-order chunks
    this.nextExpectedChunk = 1        // Next chunk number we expect to process

    // Web Audio state for PCM playback
    this.audioContext = null
    this.playbackCursor = 0
    this.scheduledSources = []
    this.pendingResumeAttempt = null
    this.ttsSampleRate = 24000
    this.pcmByteRemainder = null

    // UI state
    this.audioAutoplayEnabled = false
    this.playbackPromptElement = null
    this.awaitingUserActivation = false
    this.userGesturePlaybackHandler = (event) => this.handleUserGestureForPlayback(event)
    this.lastChunkNumber = null

    // Subscribe to TTS audio streaming channel
    this.subscribeTTSChannel()
  }

  disconnect() {
    // Cleanup when controller is disconnected
    if (this.ttsChannel) {
      this.ttsChannel.unsubscribe()
    }
    window.removeEventListener("click", this.userGesturePlaybackHandler)
    this.cleanupAudioPlayback()
  }

  subscribeTTSChannel() {
    // Import ActionCable and subscribe to BartenderTtsChannel
    import("@rails/actioncable").then(({ createConsumer }) => {
      const consumer = createConsumer()

      this.ttsChannel = consumer.subscriptions.create("BartenderTtsChannel", {
        connected: () => {
          console.log("[TTS] Connected to BartenderTtsChannel")
        },

        disconnected: () => {
          console.log("[TTS] Disconnected from BartenderTtsChannel")
        },

        received: (data) => {
          this.handleTTSMessage(data)
        }
      })
    }).catch(error => {
      console.error("[TTS] Failed to load ActionCable:", error)
    })
  }

  handleTTSMessage(data) {
    console.log("[TTS] Received message:", data.type, data)
    switch (data.type) {
      case 'stream_start':
        this.handleStreamStart(data)
        break
      case 'audio_chunk':
        this.handleAudioChunk(data)
        break
      case 'stream_complete':
        this.handleStreamComplete(data)
        break
      case 'stream_error':
        this.handleStreamError(data)
        break
      case 'cancelled':
        this.handleStreamCancelled(data)
        break
      default:
        console.warn("[TTS] Unknown message type:", data.type)
    }
  }

  handleStreamStart(data) {
    console.log("[TTS] Stream starting for response:", data.response_id)
    this.startTtsForResponse(data.response_id, data.format)
  }

  handleAudioChunk(data) {
    console.log("[TTS] Received chunk:", data.chunk_number, "for response:", data.response_id)
    if (data.format && data.format !== this.currentTtsFormat) {
      console.log("[TTS] Updating TTS format from chunk metadata:", data.format)
      this.currentTtsFormat = data.format
      if (this.isPcmPlaybackMode()) {
        this.initializeAudioContext()
      }
    }
    this.processAudioChunk(data)
  }

  handleStreamComplete(data) {
    console.log("[TTS] Stream complete for response:", data.response_id, "chunks:", data.chunks_sent)
    this.finalizeAudioPlayback(data)
  }

  handleStreamError(data) {
    console.error("[TTS] Stream error:", data.error)

    // Clear the queue and stop playback
    this.cleanupAudioPlayback()

    // Show error to user
    this.addMessageToUI("error", `Voice playback failed: ${data.error}`)
  }

  handleStreamCancelled(data) {
    // Clear the queue and stop playback
    if (data.response_id === this.currentResponseId) {
      this.cleanupAudioPlayback()
    }
  }

  // ====== Simple buffered playback helpers ======

  startTtsForResponse(responseId, format = null) {
    if (!responseId) return

    const isSameResponse = this.currentResponseId === responseId

    if (this.currentResponseId && !isSameResponse) {
      // Different response, clean up previous
      console.log("[TTS] New response starting, cleaning up previous")
      this.cleanupAudioPlayback()
    }

    this.currentResponseId = responseId

    if (format) {
      this.currentTtsFormat = format
    } else if (!isSameResponse) {
      this.currentTtsFormat = "pcm"
    }

    // Reset streaming state for new response
    this.streamComplete = false
    this.lastChunkNumber = null
    this.queuedChunkCount = 0
    this.playedChunkCount = 0
    this.playbackStarted = false
    this.playbackCursor = 0
    this.chunkBuffer.clear()
    this.nextExpectedChunk = 1
    this.pcmByteRemainder = null

    if (this.isPcmPlaybackMode()) {
      this.initializeAudioContext()
    }

    console.log("[TTS] Initialized TTS for response:", responseId, "format:", this.currentTtsFormat)
  }

  processAudioChunk({ response_id, chunk, chunk_number, format = null }) {
    if (this.currentResponseId && response_id !== this.currentResponseId) {
      console.warn("[TTS] Ignoring chunk for old response_id", response_id)
      return
    }

    if (format && format !== this.currentTtsFormat) {
      console.log("[TTS] Process chunk updating format:", format)
      this.currentTtsFormat = format
      if (this.isPcmPlaybackMode()) {
        this.initializeAudioContext()
      }
    }

    if (typeof chunk_number !== "number") {
      console.warn("[TTS] Invalid chunk_number:", chunk_number)
      return
    }

    // Check if this is a duplicate chunk we've already processed
    if (chunk_number < this.nextExpectedChunk) {
      console.warn("[TTS] Dropping duplicate chunk", chunk_number, "already processed")
      return
    }

    try {
      const bytes = this.base64ToUint8Array(chunk)

      if (chunk_number === this.nextExpectedChunk) {
        // This is the chunk we're waiting for - process it immediately
        console.log(`[TTS] Processing chunk ${chunk_number} in order`)
        this.createAndQueueAudioChunk(bytes, chunk_number)
        this.nextExpectedChunk++

        // Check if we have any buffered chunks that can now be processed
        this.processBufferedChunks()
      } else {
        // This chunk arrived early - buffer it for later
        console.log(`[TTS] Buffering out-of-order chunk ${chunk_number} (expecting ${this.nextExpectedChunk})`)
        this.chunkBuffer.set(chunk_number, bytes)
      }

    } catch (error) {
      console.error("[TTS] Failed to process chunk:", error)
    }
  }

  processBufferedChunks() {
    // Process any consecutive chunks that are now available in the buffer
    while (this.chunkBuffer.has(this.nextExpectedChunk)) {
      const chunkNumber = this.nextExpectedChunk
      const bytes = this.chunkBuffer.get(chunkNumber)

      console.log(`[TTS] Processing buffered chunk ${chunkNumber}`)
      this.createAndQueueAudioChunk(bytes, chunkNumber)

      this.chunkBuffer.delete(chunkNumber)
      this.nextExpectedChunk++
    }
  }

  createAndQueueAudioChunk(chunkBytes, chunkNumber) {
    if (!chunkBytes || chunkBytes.length === 0) {
      console.warn(`[TTS] Ignoring empty audio chunk ${chunkNumber}`)
      return
    }

    if (!this.isPcmPlaybackMode()) {
      console.error("[TTS] Received chunk but format is not PCM; cannot play in streaming mode")
      return
    }

    this.queuePcmAudioChunk(chunkBytes, chunkNumber)
  }


  isPcmPlaybackMode() {
    if (!this.currentTtsFormat) return false
    const format = this.currentTtsFormat.toLowerCase()
    return format.includes("pcm") || format === "wav"
  }

  initializeAudioContext() {
    if (this.audioContext) return

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      if (!AudioContextClass) {
        console.error("[TTS] Web Audio API not supported in this browser")
        return
      }
      this.audioContext = new AudioContextClass({ sampleRate: this.ttsSampleRate })
      this.playbackCursor = this.audioContext.currentTime
      this.scheduledSources = []
      console.log("[TTS] AudioContext initialized for PCM playback")
    } catch (error) {
      console.error("[TTS] Failed to initialize AudioContext:", error)
      this.audioContext = null
    }
  }

  queuePcmAudioChunk(chunkBytes, chunkNumber) {
    this.initializeAudioContext()
    if (!this.audioContext) {
      console.warn("[TTS] AudioContext unavailable, cannot queue PCM chunk")
      return
    }

    const pcm16 = this.convertBytesToPcm16(chunkBytes)
    if (!pcm16) return

    const audioBuffer = this.pcm16ToAudioBuffer(pcm16)
    if (!audioBuffer) return

    const queueItem = { chunkNumber, audioBuffer }
    this.audioQueue.push(queueItem)
    this.queuedChunkCount++

    console.log(`[TTS] Queued PCM chunk ${chunkNumber}, queue size: ${this.audioQueue.length}`)

    this.processPcmQueue()
  }

  convertBytesToPcm16(chunkBytes) {
    let combinedBytes = chunkBytes

    if (this.pcmByteRemainder) {
      combinedBytes = new Uint8Array(this.pcmByteRemainder.length + chunkBytes.length)
      combinedBytes.set(this.pcmByteRemainder, 0)
      combinedBytes.set(chunkBytes, this.pcmByteRemainder.length)
      this.pcmByteRemainder = null
    }

    if (combinedBytes.byteLength % 2 !== 0) {
      this.pcmByteRemainder = combinedBytes.slice(combinedBytes.byteLength - 1)
      combinedBytes = combinedBytes.slice(0, combinedBytes.byteLength - 1)
    }

    if (combinedBytes.byteLength === 0) {
      return null
    }

    const view = new DataView(combinedBytes.buffer, combinedBytes.byteOffset, combinedBytes.byteLength)
    const sampleCount = combinedBytes.byteLength / 2
    const pcm16 = new Int16Array(sampleCount)

    for (let i = 0; i < sampleCount; i++) {
      pcm16[i] = view.getInt16(i * 2, true)
    }

    return pcm16
  }

  pcm16ToAudioBuffer(pcm16) {
    if (!this.audioContext) return null

    try {
      const buffer = this.audioContext.createBuffer(1, pcm16.length, this.audioContext.sampleRate)
      const channel = buffer.getChannelData(0)
      for (let i = 0; i < pcm16.length; i++) {
        channel[i] = pcm16[i] / 32768
      }
      return buffer
    } catch (error) {
      console.error("[TTS] Failed to convert PCM chunk to AudioBuffer:", error)
      return null
    }
  }

  processPcmQueue() {
    if (!this.isPcmPlaybackMode() || !this.audioContext) return

    if (this.audioQueue.length === 0) {
      this.handlePcmPlaybackCompletionIfDone()
      return
    }

    // Wait for initial buffer before starting playback (prevents garbled audio at start)
    if (!this.playbackStarted && this.audioQueue.length < this.initialBufferSize && !this.streamComplete) {
      console.log(`[TTS] Buffering initial chunks (${this.audioQueue.length}/${this.initialBufferSize})`)
      return
    }

    if (this.audioContext.state !== "running") {
      if (!this.awaitingUserActivation) {
        this.awaitingUserActivation = true
        this.showPlaybackPrompt()
        window.addEventListener("click", this.userGesturePlaybackHandler, { once: true })
      } else {
        this.showPlaybackPrompt()
      }

      if (!this.pendingResumeAttempt) {
        this.pendingResumeAttempt = this.audioContext.resume()
          .then(() => {
            this.pendingResumeAttempt = null
            this.audioAutoplayEnabled = true
            this.hidePlaybackPrompt()
            this.processPcmQueue()
          })
          .catch(error => {
            console.warn("[TTS] AudioContext resume blocked:", error)
            this.pendingResumeAttempt = null
          })
      }
      return
    }

    if (!this.playbackStarted) {
      console.log(`[TTS] Starting playback with ${this.audioQueue.length} buffered chunks`)
    }

    this.playbackStarted = true
    this.isPlaybackActive = true

    while (this.audioQueue.length > 0) {
      const nextItem = this.audioQueue.shift()
      this.schedulePcmBuffer(nextItem)
    }
  }

  schedulePcmBuffer({ audioBuffer, chunkNumber }) {
    if (!this.audioContext || !audioBuffer) return

    // Apply fade-in to the very first chunk to prevent audio clipping
    const isFirstChunk = (chunkNumber === 1)
    const bufferToPlay = isFirstChunk ? this.applyFadeIn(audioBuffer, 0.05) : audioBuffer

    const source = this.audioContext.createBufferSource()
    source.buffer = bufferToPlay
    source.connect(this.audioContext.destination)

    const now = this.audioContext.currentTime
    if (this.playbackCursor < now) {
      this.playbackCursor = now
    }

    const startTime = this.playbackCursor
    source.start(startTime)
    this.playbackCursor += bufferToPlay.duration

    console.log(`[TTS] Scheduled PCM chunk ${chunkNumber} at ${startTime.toFixed(3)}s (${bufferToPlay.duration.toFixed(3)}s)${isFirstChunk ? ' with fade-in' : ''}`)

    source.onended = () => {
      this.playedChunkCount++
      this.scheduledSources = this.scheduledSources.filter(active => active !== source)
      this.handlePcmPlaybackCompletionIfDone()
    }

    source.onerror = (error) => {
      console.error(`[TTS] PCM chunk ${chunkNumber} playback error:`, error)
      this.playedChunkCount++
      this.scheduledSources = this.scheduledSources.filter(active => active !== source)
      this.handlePcmPlaybackCompletionIfDone()
    }

    this.scheduledSources.push(source)
  }

  applyFadeIn(audioBuffer, fadeSeconds = 0.05) {
    if (!this.audioContext) return audioBuffer

    // Create a copy of the buffer
    const fadedBuffer = this.audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      audioBuffer.length,
      this.audioContext.sampleRate
    )

    const fadeSamples = Math.floor(fadeSeconds * this.audioContext.sampleRate)

    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const originalData = audioBuffer.getChannelData(channel)
      const fadedData = fadedBuffer.getChannelData(channel)

      for (let i = 0; i < audioBuffer.length; i++) {
        if (i < fadeSamples) {
          // Linear fade-in
          const fadeMultiplier = i / fadeSamples
          fadedData[i] = originalData[i] * fadeMultiplier
        } else {
          fadedData[i] = originalData[i]
        }
      }
    }

    return fadedBuffer
  }

  handlePcmPlaybackCompletionIfDone() {
    if (!this.isPcmPlaybackMode()) return

    if (this.audioQueue.length === 0 && this.scheduledSources.length === 0) {
      if (this.streamComplete) {
        console.log("[TTS] PCM playback complete - all chunks played")
        this.hidePlaybackPrompt()
      }
      this.isPlaybackActive = false
    }
  }

  resumeAudioContextAfterGesture() {
    if (!this.audioContext) return
    this.audioContext.resume()
      .then(() => {
        console.log("[TTS] AudioContext resumed after user gesture")
        this.audioAutoplayEnabled = true
        this.processPcmQueue()
      })
      .catch(error => {
        console.error("[TTS] Failed to resume AudioContext after user gesture:", error)
      })
  }

  finalizeAudioPlayback({ response_id, chunks_sent, duration }) {
    console.log("[TTS] Stream complete - response_id:", response_id)
    console.log("[TTS] Expected chunks:", chunks_sent, "Queued:", this.queuedChunkCount, "Played:", this.playedChunkCount)

    if (this.currentResponseId && response_id !== this.currentResponseId) {
      console.warn("[TTS] Ignoring stream_complete for old response_id", response_id)
      return
    }

    // Mark stream as complete
    this.streamComplete = true

    // Validate chunk count
    if (this.queuedChunkCount === 0) {
      console.warn("[TTS] No audio chunks received for this stream")
      return
    }

    if (this.queuedChunkCount < chunks_sent) {
      console.warn(`[TTS] Missing chunks: expected ${chunks_sent}, got ${this.queuedChunkCount}`)
    }

    // Edge case: Stream completed before playback started
    // This can happen if all chunks arrive very quickly
    if (!this.playbackStarted && this.audioQueue.length > 0) {
      console.log("[TTS] Stream complete before playback started, scheduling queued PCM buffers now")
      this.processPcmQueue()
    }

    // Log metrics for monitoring
    console.log("[TTS Metrics]", {
      response_id: response_id,
      total_chunks_expected: chunks_sent,
      total_chunks_queued: this.queuedChunkCount,
      total_chunks_played: this.playedChunkCount,
      chunks_remaining: this.audioQueue.length,
      playback_started: this.playbackStarted,
      stream_duration: duration
    })

    console.log("[TTS] Stream finalized, PCM playback will finish naturally")
  }

  cleanupAudioPlayback() {
    console.log("[TTS] Cleaning up audio playback, queue size:", this.audioQueue.length)

    // Clean up queued/scheduled PCM buffers
    if (this.scheduledSources.length > 0) {
      this.scheduledSources.forEach(source => {
        try {
          source.stop()
        } catch (error) {
          console.warn("[TTS] Error stopping scheduled PCM source:", error)
        }
      })
      this.scheduledSources = []
    }

    if (this.audioContext) {
      if (typeof this.audioContext.close === "function") {
        this.audioContext.close().catch(() => { /* Ignore close errors */ })
      }
      this.audioContext = null
    }

    // Reset all state
    this.audioQueue = []
    this.isPlaybackActive = false
    this.playbackStarted = false
    this.queuedChunkCount = 0
    this.playedChunkCount = 0
    this.streamComplete = false
    this.currentResponseId = null
    this.currentTtsFormat = "pcm"
    this.lastChunkNumber = null
    this.chunkBuffer.clear()
    this.nextExpectedChunk = 1
    this.playbackCursor = 0
    this.pendingResumeAttempt = null
    this.pcmByteRemainder = null
    this.hidePlaybackPrompt()

    console.log("[TTS] Cleanup complete")
  }

  base64ToUint8Array(base64) {
    const binary = atob(base64)
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  }

  showPlaybackPrompt() {
    if (this.playbackPromptElement || !this.messagesTarget) return

    const wrapper = document.createElement("div")
    wrapper.className = "mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"

    const label = document.createElement("span")
    label.textContent = "Tap to enable voice playback"

    const button = document.createElement("button")
    button.type = "button"
    button.className = "px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
    button.textContent = "Play voice reply"
    button.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.hidePlaybackPrompt()
      this.resumeAudioContextAfterGesture()
    })

    wrapper.appendChild(label)
    wrapper.appendChild(button)
    this.messagesTarget.appendChild(wrapper)
    this.playbackPromptElement = wrapper
    this.scrollToBottom()
  }

  hidePlaybackPrompt() {
    if (this.playbackPromptElement) {
      this.playbackPromptElement.remove()
      this.playbackPromptElement = null
    }
    this.awaitingUserActivation = false
    window.removeEventListener("click", this.userGesturePlaybackHandler)
  }

  handleUserGestureForPlayback(event) {
    if (!this.awaitingUserActivation) {
      return
    }

    console.log("[TTS] User gesture detected, retrying playback")
    this.awaitingUserActivation = false
    this.hidePlaybackPrompt()

    this.resumeAudioContextAfterGesture()
  }

  submitMessage(event) {
    event.preventDefault()
    this.handleSubmit()
  }

  handleSubmit(event) {
    if (event) event.preventDefault()

    const message = this.inputTarget.value.trim()
    if (!message) return

    // Add user message to UI
    this.addMessageToUI("user", message)

    // Clear input
    this.inputTarget.value = ""
    this.inputTarget.focus()

    // Show loading indicator
    this.element.classList.add("loading")

    // Send message to server
    this.sendMessage(message)
  }

  sendMessage(message) {
    // Show loading indicator with timer
    this.showLoadingIndicator()

    fetch("/digital_bartender/send_message", {
      method: "POST",
      body: JSON.stringify({ message: message }),
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCsrfToken()
      }
    })
      .then(response => {
        // Log response for debugging
        console.log("Response status:", response.status)

        // Always try to parse JSON, even for error responses
        return response.json().then(data => {
          return { ok: response.ok, status: response.status, data }
        })
      })
      .then(({ ok, status, data }) => {
        console.log("Response data:", data)

        if (data.success) {
          // Add AI response to UI (use HTML version if available for rich content)
          const responseContent = data.response_html || data.response
          this.addMessageToUI("assistant", responseContent, true, data) // true = isHTML, pass data for flight button
          this.scrollToBottom()

          if (data.response_id) {
            this.startTtsForResponse(data.response_id)
          }
        } else {
          // Show the actual error message from the server
          const errorMsg = data.error || `Server error (${status}): Something went wrong`
          this.addMessageToUI("error", errorMsg)
        }
      })
      .catch(error => {
        console.error("Error:", error)
        // Provide more specific error message based on error type
        let errorMessage = "Connection error. Please check your internet connection and try again."
        if (error.name === "TypeError" && error.message.includes("fetch")) {
          errorMessage = "Network error. Please check your internet connection."
        } else if (error.message) {
          errorMessage = `Error: ${error.message}`
        }
        this.addMessageToUI("error", errorMessage)
      })
      .finally(() => {
        this.hideLoadingIndicator()
        this.element.classList.remove("loading")
      })
  }

  clearChat(event) {
    if (event) event.preventDefault()

    if (!confirm("Are you sure you want to clear the conversation?")) return

    fetch("/digital_bartender/clear", {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.getCsrfToken()
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Clear messages from UI
          this.messagesTarget.innerHTML = `
            <div class="text-center text-muted-foreground py-8">
              <p class="text-lg">👋 Conversation cleared</p>
              <p class="text-sm mt-2">Start a new conversation below</p>
            </div>
          `
          this.inputTarget.value = ""
          this.inputTarget.focus()
        }
      })
      .catch(error => console.error("Error:", error))
  }

  addMessageToUI(role, content, isHTML = false, data = null) {
    const messageDiv = document.createElement("div")
    messageDiv.className = this.getMessageClass(role)

    const contentDiv = document.createElement("div")
    contentDiv.className = this.getContentClass(role)

    // Use innerHTML for assistant messages with rich content (bottle cards, etc.)
    // Use textContent for user messages for security
    if (isHTML && role === "assistant") {
      contentDiv.innerHTML = content
    } else {
      contentDiv.textContent = content
    }

    messageDiv.appendChild(contentDiv)

    // Clear placeholder if this is the first message
    const placeholder = this.messagesTarget.querySelector(".text-center.text-muted-foreground")
    if (placeholder) placeholder.remove()

    this.messagesTarget.appendChild(messageDiv)

    // Display flight result card when CreateFlightTool succeeds
    // Append as separate full-width element below the chat bubble
    if (role === "assistant" && data?.created_flight) {
      // Debug logging to confirm payload is present and card renders
      console.log("[BartenderChat] Rendering flight card payload:", data.created_flight)
      try {
        const flightCardHTML = this.renderFlightResultCard(data.created_flight)
        const flightCardWrapper = document.createElement("div")
        flightCardWrapper.className = "mb-4 px-2"
        flightCardWrapper.innerHTML = flightCardHTML
        this.messagesTarget.appendChild(flightCardWrapper)
        console.log("[BartenderChat] Flight card appended to chat")
      } catch (err) {
        console.error("[BartenderChat] Failed to render flight card:", err, data.created_flight)
      }
    }
  }

  getMessageClass(role) {
    const baseClass = "flex mb-4"

    switch (role) {
      case "user":
        return `${baseClass} justify-end`
      case "assistant":
        return `${baseClass} justify-start`
      case "error":
        return `${baseClass} justify-start`
      default:
        return baseClass
    }
  }

  getContentClass(role) {
    const baseClass = "px-4 py-3 rounded-lg text-sm"

    switch (role) {
      case "user":
        return `${baseClass} max-w-xs lg:max-w-md bg-primary text-primary-foreground rounded-br-none`
      case "assistant":
        // Wider for assistant to accommodate bottle cards and rich content
        return `${baseClass} max-w-md lg:max-w-2xl bg-muted text-foreground rounded-bl-none`
      case "error":
        return `${baseClass} max-w-xs lg:max-w-md bg-destructive/10 text-destructive border border-destructive/50 rounded-bl-none`
      default:
        return baseClass
    }
  }

  // Render a flight result card for CreateFlightTool success
  renderFlightResultCard(flightData) {
    const { flight_id, flight_url, flight_name, bottles, blind, start_session_url } = flightData

    // Sort bottles by position
    const sortedBottles = [...bottles].sort((a, b) => a.position - b.position)

    const bottleList = sortedBottles.map(bottle => `
      <div class="flex items-center gap-2 py-1.5 border-b border-border/50 last:border-0">
        <span class="flex-shrink-0 w-6 h-6 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium flex items-center justify-center">
          ${bottle.position}
        </span>
        <span class="text-sm text-foreground">${this.escapeHtml(bottle.name)}</span>
      </div>
    `).join('')

    return `
      <div class="flight-result-card mt-3 bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <!-- Header -->
        <div class="bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 border-b border-border">
          <div class="flex items-center gap-2">
            <span class="text-emerald-600 dark:text-emerald-400 text-lg">✅</span>
            <h3 class="font-semibold text-foreground">Flight Created!</h3>
          </div>
          <p class="text-sm text-muted-foreground mt-1">${this.escapeHtml(flight_name)}</p>
          ${blind ? '<span class="inline-block mt-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs rounded-full">🙈 Blind Tasting</span>' : ''}
        </div>

        <!-- Bottle List -->
        <div class="px-4 py-3">
          <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Whiskeys in Flight</p>
          <div class="space-y-0">
            ${bottleList}
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="px-4 py-3 bg-muted/30 border-t border-border flex flex-wrap gap-2">
          <a href="${flight_url}" class="px-3 py-1.5 bg-secondary text-secondary-foreground text-sm rounded-md hover:bg-secondary/80 transition-colors">
            View Flight
          </a>
          <a href="${flight_url}/edit" class="px-3 py-1.5 bg-secondary text-secondary-foreground text-sm rounded-md hover:bg-secondary/80 transition-colors">
            Edit Flight
          </a>
          <button type="button"
                  class="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition-colors font-medium"
                  data-action="click->bartender-chat#startTastingSession"
                  data-start-session-url="${start_session_url}"
                  data-flight-name="${this.escapeHtml(flight_name)}">
            🥃 Start Tasting
          </button>
        </div>
      </div>
    `
  }

  // Handle Start Tasting button click - POST to create session and redirect
  startTastingSession(event) {
    const button = event.target
    const startSessionUrl = button.dataset.startSessionUrl
    const flightName = button.dataset.flightName

    // Disable button during request
    button.disabled = true
    const originalText = button.innerHTML
    button.innerHTML = "Starting..."

    fetch(startSessionUrl, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.getCsrfToken(),
        "Accept": "application/json"
      }
    })
      .then(response => {
        if (response.redirected) {
          // Server returned a redirect, follow it
          window.location.href = response.url
          return
        }
        return response.json()
      })
      .then(data => {
        if (data && data.redirect_url) {
          // JSON response with redirect URL
          window.location.href = data.redirect_url
        } else if (data && data.error) {
          alert(`Failed to start session: ${data.error}`)
          button.disabled = false
          button.innerHTML = originalText
        }
      })
      .catch(error => {
        console.error("Error starting tasting session:", error)
        alert("Connection error. Please try again.")
        button.disabled = false
        button.innerHTML = originalText
      })
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // Voice/Vibe selection handlers
  handleVoiceChange(event) {
    this.selectedVoice = event.target.value
    console.log("Voice changed to:", this.selectedVoice)

    // Persist to session
    fetch("/digital_bartender/set_voice", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCsrfToken()
      },
      body: JSON.stringify({ voice: this.selectedVoice })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log("Voice preference saved:", data.voice)
        } else {
          console.error("Failed to save voice:", data.error)
        }
      })
      .catch(error => console.error("Error saving voice:", error))
  }

  handleVibeChange(event) {
    this.selectedVibe = event.target.value
    console.log("Vibe changed to:", this.selectedVibe)

    // Persist to session
    fetch("/digital_bartender/set_vibe", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCsrfToken()
      },
      body: JSON.stringify({ vibe: this.selectedVibe })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log("Vibe preference saved:", data.vibe)
        } else {
          console.error("Failed to save vibe:", data.error)
        }
      })
      .catch(error => console.error("Error saving vibe:", error))
  }

  // Recording flow methods
  async startRecording(event) {
    if (event) event.preventDefault()

    if (this.recordingState !== "idle") {
      console.warn("Already recording or processing")
      return
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Setup MediaRecorder with webm/opus codec
      const options = { mimeType: "audio/webm;codecs=opus" }
      this.mediaRecorder = new MediaRecorder(stream, options)
      this.audioChunks = []

      // Collect audio data
      this.mediaRecorder.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
        }
      })

      // Handle recording completion
      this.mediaRecorder.addEventListener("stop", () => {
        this.handleRecordingComplete()
      })

      // Start recording
      this.mediaRecorder.start()
      this.recordingState = "recording"
      this.recordingStartTime = Date.now()

      // Update UI
      this.updateRecordingUI()

      // Start timer
      this.startTimer()

      console.log("Recording started")
    } catch (error) {
      console.error("Error starting recording:", error)

      // Handle permission errors
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        alert(
          "Microphone access denied.\n\n" +
          "Please allow microphone access in your browser settings to use voice recording.\n\n" +
          "You can still type your message instead."
        )
      } else {
        alert("Failed to start recording. Please try again or use text input.")
      }

      this.resetRecordingState()
    }
  }

  stopRecording(event) {
    if (event) event.preventDefault()

    if (this.recordingState !== "recording") {
      console.warn("Not currently recording")
      return
    }

    console.log("Stopping recording...")

    // Stop the MediaRecorder
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
    }

    // Stop all tracks to release the microphone
    if (this.mediaRecorder && this.mediaRecorder.stream) {
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
    }

    // Stop timer
    this.stopTimer()

    // Update state
    this.recordingState = "processing"
    this.updateRecordingUI()
  }

  handleRecordingComplete() {
    console.log("Recording complete, processing audio...")

    // Create audio blob
    const audioBlob = new Blob(this.audioChunks, { type: "audio/webm;codecs=opus" })
    const audioSize = audioBlob.size

    console.log(`Audio blob created: ${(audioSize / 1024).toFixed(2)} KB`)

    // Check size limit (5 MB)
    const maxSizeBytes = 5 * 1024 * 1024
    if (audioSize > maxSizeBytes) {
      alert(
        "Recording is too large (max 5 MB).\n\n" +
        "Please record a shorter message or use text input."
      )
      this.resetRecordingState()
      return
    }

    // Send audio for transcription (loading indicator will be shown in sendAudioForTranscription)
    this.sendAudioForTranscription(audioBlob)
      .then(() => {
        this.resetRecordingState()
      })
      .catch(error => {
        console.error("Transcription error:", error)
        alert("Failed to transcribe audio. Please try again or use text input.")
        this.resetRecordingState()
      })
  }

  async sendAudioForTranscription(audioBlob) {
    // Show loading indicator
    this.showLoadingIndicator()

    try {
      const formData = new FormData()
      formData.append("audio", audioBlob, "recording.webm")
      formData.append("voice", this.selectedVoice)
      formData.append("vibe", this.selectedVibe)

      const response = await fetch("/digital_bartender/transcribe", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: formData
      })

      const data = await response.json()

      if (data.success) {
        // Display the user's transcribed message
        this.addMessageToUI("user", data.transcript)

        // Display the assistant's response (with embedded bottle cards if present)
        // Pass full payload so flight result cards render (CreateFlightTool)
        const responseContent = data.response_html || data.response
        this.addMessageToUI("assistant", responseContent, true, data)

        this.scrollToBottom()

        // TTS streaming is handled automatically via ActionCable subscription
        if (data.response_id) {
          console.log("[TTS] Response ID received, audio will stream via ActionCable:", data.response_id)
          this.startTtsForResponse(data.response_id)
        }
      } else {
        throw new Error(data.error || "Transcription failed")
      }
    } finally {
      this.hideLoadingIndicator()
    }
  }

  startTimer() {
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000)
      const minutes = Math.floor(elapsed / 60)
      const seconds = elapsed % 60
      const display = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`

      this.timerDisplayTarget.textContent = display

      // Auto-stop at max duration
      if (elapsed >= this.maxRecordingSeconds) {
        console.log("Max recording time reached, auto-stopping...")
        this.stopRecording()
        alert(`Maximum recording time (${this.maxRecordingSeconds}s) reached. Recording stopped automatically.`)
      }
    }, 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  updateRecordingUI() {
    if (!this.hasRecordButtonTarget) return

    switch (this.recordingState) {
      case "idle":
        // Show record button, hide stop button and timer
        this.recordButtonTarget.classList.remove("hidden")
        this.recordButtonTarget.disabled = false
        if (this.hasStopButtonTarget) {
          this.stopButtonTarget.classList.add("hidden")
        }
        if (this.hasTimerTarget) {
          this.timerTarget.classList.add("hidden")
          this.timerTarget.classList.remove("flex")
        }
        // Enable text input and send button
        this.inputTarget.disabled = false
        if (this.hasSendButtonTarget) {
          this.sendButtonTarget.disabled = false
        }
        break

      case "recording":
        // Hide record button, show stop button and timer
        this.recordButtonTarget.classList.add("hidden")
        if (this.hasStopButtonTarget) {
          this.stopButtonTarget.classList.remove("hidden")
          this.stopButtonTarget.disabled = false
        }
        if (this.hasTimerTarget) {
          this.timerTarget.classList.remove("hidden")
          this.timerTarget.classList.add("flex")
        }
        // Disable text input and send button during recording
        this.inputTarget.disabled = true
        if (this.hasSendButtonTarget) {
          this.sendButtonTarget.disabled = true
        }
        break

      case "processing":
        // Disable all controls during processing
        this.recordButtonTarget.classList.add("hidden")
        if (this.hasStopButtonTarget) {
          this.stopButtonTarget.classList.add("hidden")
        }
        if (this.hasTimerTarget) {
          this.timerTarget.classList.remove("hidden")
          this.timerTarget.classList.add("flex")
          // Could add a processing indicator here
        }
        this.inputTarget.disabled = true
        if (this.hasSendButtonTarget) {
          this.sendButtonTarget.disabled = true
        }
        break
    }
  }

  resetRecordingState() {
    this.recordingState = "idle"
    this.mediaRecorder = null
    this.audioChunks = []
    this.recordingStartTime = null
    this.stopTimer()

    // Reset timer display
    if (this.hasTimerDisplayTarget) {
      this.timerDisplayTarget.textContent = "00:00"
    }

    // Update UI
    this.updateRecordingUI()
  }

  // Loading indicator methods
  showLoadingIndicator() {
    if (this.loadingElement) {
      this.hideLoadingIndicator()
    }

    // Create loading element with animated timer
    const loadingDiv = document.createElement("div")
    loadingDiv.className = "mb-3 flex items-center gap-2 text-sm text-muted-foreground animate-pulse"

    const spinner = document.createElement("div")
    spinner.className = "animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"

    const label = document.createElement("span")
    label.className = "loading-text"
    label.textContent = "Thinking"

    const timer = document.createElement("span")
    timer.className = "loading-timer font-mono text-xs"
    timer.textContent = "0s"

    loadingDiv.appendChild(spinner)
    loadingDiv.appendChild(label)
    loadingDiv.appendChild(timer)

    this.messagesTarget.appendChild(loadingDiv)
    this.loadingElement = loadingDiv
    this.loadingStartTime = Date.now()

    // Update timer every second
    this.loadingInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.loadingStartTime) / 1000)
      timer.textContent = `${elapsed}s`

      // Add dots animation to "Thinking"
      const dots = ".".repeat((elapsed % 4))
      label.textContent = `Thinking${dots}`

      // Warn if taking too long
      if (elapsed === 30) {
        label.textContent = "Still working on it"
      } else if (elapsed === 50) {
        label.textContent = "Almost there"
      }
    }, 1000)

    this.scrollToBottom()
  }

  hideLoadingIndicator() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval)
      this.loadingInterval = null
    }

    if (this.loadingElement) {
      this.loadingElement.remove()
      this.loadingElement = null
    }

    this.loadingStartTime = null
  }
}
